// LanguageButton.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import getCurrentLanguage, {
  getLanguageTranslationKey,
} from '../resource/getCurrentLanguage.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';
import { breakpoint, color } from '../style/variables.js';

import LanguageTealblueIconSource from '../../img/ic_language_tealblue.svg';
import LanguageWhiteIconSource from '../../img/ic_language_white.svg';
import NextTealblueIconSource from '../../img/ic_next_tealblue.svg';
import NextWhiteIconSource from '../../img/ic_next_white.svg';

import { NULL_FUNCTION } from '../resource/defaults.js';

export const LayoutType = {
  HAMBURGER: 'hamburger',
  LOGIN_SIGNUP: 'loginSignup',
};

export const LanguageButton = ({
  modalViewRootId = null,
  layoutType = null,
  isLanguageMenuOpened = false,
  deviceWidth = 0,
  t,
  i18n,
  toggleModal = NULL_FUNCTION,
}) => {
  const isHamburger = LayoutType.HAMBURGER === layoutType;
  const isLoginSignup = LayoutType.LOGIN_SIGNUP === layoutType;
  const isMobile = deviceWidth <= breakpoint.mobile;
  const closeModalOnItemClick = isMobile
    ? NULL_FUNCTION
    : () =>
        toggleModal({
          modalId: 'HamburgerMenuModal',
          isOpen: false,
        });

  return (
    <StyledLanguageButton
      isHamburger={isHamburger}
      isLoginSignup={isLoginSignup}
      id={modalViewRootId}
      data-element_id={ButtonId.Setting.ButtonLanguage}
      onClick={event => {
        event.stopPropagation();
        closeModalOnItemClick();
        return toggleModal({
          modalId: 'LanguageMenu',
          isOpen: !isLanguageMenuOpened,
          payload: {
            modalViewRootId,
            layoutType,
          },
          modalOption: { isHigherThanAll: true },
        });
      }}
      isMenuOpened={isLanguageMenuOpened}
    >
      <Title
        isHamburger={isHamburger}
        isLoginSignup={isLoginSignup}
        isMenuOpened={isLanguageMenuOpened}
      >
        <LanguageIcon
          isHamburger={isHamburger}
          isLoginSignup={isLoginSignup}
          alt="language icon"
          src={
            isHamburger
              ? LanguageWhiteIconSource.src || LanguageWhiteIconSource
              : isLanguageMenuOpened
                ? LanguageTealblueIconSource.src || LanguageTealblueIconSource
                : LanguageWhiteIconSource.src || LanguageWhiteIconSource
          }
        />
        {isHamburger ? (
          <span>
            {t('language', {
              ns: TranslationNamespace.SETTINGS,
            })}
          </span>
        ) : null}
      </Title>
      <SelectedLanguage
        isHamburger={isHamburger}
        isLoginSignup={isLoginSignup}
        isMenuOpened={isLanguageMenuOpened}
      >
        <span>
          {t(
            getLanguageTranslationKey({
              language: i18n.language || getCurrentLanguage(),
            }),
            {
              ns: TranslationNamespace.SETTINGS,
            }
          )}
        </span>
        {!isHamburger && (
          <NextIcon
            isLoginSignup={isLoginSignup}
            width={isLoginSignup ? 14 : 24}
            height={isLoginSignup ? 14 : 24}
            alt="switch language"
            src={
              isLanguageMenuOpened
                ? NextTealblueIconSource.src || NextTealblueIconSource
                : NextWhiteIconSource.src || NextWhiteIconSource
            }
          />
        )}
      </SelectedLanguage>
    </StyledLanguageButton>
  );
};

LanguageButton.propTypes = {
  modalViewRootId: PropTypes.string,
  layoutType: PropTypes.string,
  isLanguageMenuOpened: PropTypes.bool,
  deviceWidth: PropTypes.number,
  i18n: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  toggleModal: PropTypes.func,
};

const StyledLanguageButton = styled.button.attrs(
  ({ isMenuOpened, isHamburger, isLoginSignup }) => ({
    style: {
      borderColor: isMenuOpened
        ? color.tealBlue
        : isLoginSignup
          ? color.neutral[20]
          : color.neutral[100],
      borderWidth: isHamburger ? 'medium' : '1px',
      borderStyle: isHamburger ? 'none' : 'solid',
      color: isMenuOpened ? color.tealBlue : color.neutral[100],
      width: isHamburger ? '100%' : 'width',
      height: isHamburger ? 'unset' : isLoginSignup ? '32px' : '36px',
      padding: isHamburger ? '0' : isLoginSignup ? '4px 8px' : '0px 8px',
      lineHeight: 1.5,
    },
  })
)`
  ${ResetButtonStyle};
  position: relative;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${({ isMenuOpened, isLoginSignup }) =>
    isLoginSignup &&
    !isMenuOpened &&
    media.mobile`
      border-color: ${color.neutral[20]};
    `}

  ${({ isLoginSignup }) =>
    isLoginSignup &&
    media.mobile`
      height: 24px;
      padding: 3px 8px;
    `}
`;

const Title = styled.div.attrs(({ isHamburger, isMenuOpened }) => ({
  style: {
    color: isHamburger
      ? color.neutral[100]
      : isMenuOpened
        ? color.tealBlue
        : color.neutral[100],
  },
}))`
  display: flex;
  align-items: center;
  font-size: ${({ isLoginSignup }) => (isLoginSignup ? '16px' : '14px')};
  font-weight: ${({ isLoginSignup }) => (isLoginSignup ? '600' : '400')};

  ${({ isLoginSignup }) =>
    isLoginSignup &&
    media.mobile`
      font-size: 12px;
    `}
`;

const LanguageIcon = styled.img.attrs(({ isHamburger }) => ({
  style: {
    marginRight: `${isHamburger ? 8 : 4}px`,
    width: isHamburger ? '24px' : '16px',
    height: isHamburger ? '24px' : '16px',
  },
}))`
  object-fit: contain;

  ${({ isHamburger, isLoginSignup }) =>
    !isHamburger &&
    isLoginSignup &&
    media.mobile`
      width: 14px;
      height: 14px;
    `}
`;

const SelectedLanguage = styled.div.attrs(({ isHamburger, isMenuOpened }) => ({
  style: {
    color: isHamburger
      ? color.neutral[30]
      : isMenuOpened
        ? color.tealBlue
        : color.neutral[100],
  },
}))`
  display: flex;
  align-items: center;
  font-size: ${({ isLoginSignup }) => (isLoginSignup ? '16px' : '14px')};
  font-weight: ${({ isLoginSignup }) => (isLoginSignup ? '600' : '400')};

  ${({ isLoginSignup }) =>
    isLoginSignup &&
    media.mobile`
      font-size: 12px;
    `}
`;

const NextIcon = styled.img.attrs(({ isLoginSignup }) => ({
  style: {
    width: isLoginSignup ? '14px' : '24px',
    height: isLoginSignup ? '14px' : '24px',
  },
}))`
  margin-left: 4px;
  object-fit: contain;
`;

export default withTranslation()(LanguageButton);
